import SearchIcon from '@mui/icons-material/Search';
import { Checkbox, FormControlLabel, TextField, Box, Typography, Divider } from '@mui/material';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtom, useAtomValue } from 'jotai';
import { Business_Profile_Enum } from '@kheops/graphql';
import SelectFilterChip from '../../common/components/SelectFilterChip';
import { arrayToggle } from '../../common/utils/common.utils';
import { signedContractsAtom, currentContextAtom } from '../../state';
import { OrderListUiState, orderListUiStateAtom } from '../state/state';

export default function OrderCompanyFilter(): React.JSX.Element {
  const { t } = useTranslation(['common']);
  const { realm } = useAtomValue(currentContextAtom);
  const contracts = useAtomValue(signedContractsAtom);
  const [orderListUiState, setOrderListUiState] = useAtom(orderListUiStateAtom);
  const [query, setQuery] = useState('');

  const handleTextChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
    setQuery(event.target.value);
  }, []);

  const handleSelectedCompanyChange = (companyId: string): void => {
    const newSelectedCompanyIds = [...orderListUiState.selectedCompanyIds];

    arrayToggle(newSelectedCompanyIds, companyId);

    const newState: OrderListUiState = {
      ...orderListUiState,
      selectedCompanyIds: newSelectedCompanyIds,
    };

    setOrderListUiState(newState);

    window.history.replaceState({
      ...window.history.state,
      uiState: {
        ...newState,
        page: 1,
      },
    }, '');
  };

  const companyOptions = useMemo((): React.JSX.Element[] => (
    contracts
      .filter(({ company }) => company.tradeName.toLowerCase().includes(query.toLowerCase()))
      .map(({ company }, index, array) => (
        <>
          <FormControlLabel
            key={company.id}
            control={<Checkbox checked={orderListUiState.selectedCompanyIds.includes(company.id)} />}
            onChange={() => handleSelectedCompanyChange(company.id)}
            label={company.tradeName}
            sx={{ ml: 0 }}
          />
          {index < array.length - 1 && <Divider />}
        </>
      ))
  ), [query, contracts, orderListUiState]);

  return (
    <SelectFilterChip
      isFilterActive={!!orderListUiState.selectedCompanyIds.length}
      label={`${realm === Business_Profile_Enum.Supplier ? t('common:customer_other') : t('common:supplier_other')} (${orderListUiState.selectedCompanyIds.length || contracts.length})`}
      content={(
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <TextField
            size="small"
            slotProps={{
              input: {
                startAdornment: <SearchIcon fontSize="small" />,
              },
            }}
            onChange={handleTextChange}
            value={query}
            placeholder={t('common:search')}
          />
          <Typography sx={{ mt: 2, mb: 1 }} variant="titleMedium">{realm === Business_Profile_Enum.Supplier ? t('search:select_one_or_several_clients') : t('search:select_one_or_several_suppliers')}</Typography>
          {companyOptions}
        </Box>
      )}
      onClose={() => setQuery('')}
    />
  );
}
